// import React from 'react';
import { Building2, HeartPulse, Code, Factory, Landmark, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

interface SolutionsPageProps {
  onNavigate: (page: string) => void;
}

export function SolutionsPage({ onNavigate }: SolutionsPageProps) {
  const solutions = [
    {
      icon: Building2,
      industry: 'Financial Services',
      description: 'Navigate complex regulatory environments with confidence',
      challenges: [
        'Regulatory compliance across multiple jurisdictions',
        'Rigorous audit requirements and documentation',
        'Risk management at portfolio level',
        'Integration with legacy systems',
      ],
      benefits: [
        'Built-in compliance frameworks for SOX, Basel III, GDPR',
        'Complete audit trails and version control',
        'Enterprise risk management dashboards',
        'Secure single-tenant architecture',
      ],
    },
    {
      icon: HeartPulse,
      industry: 'Healthcare',
      description: 'Deliver clinical excellence while maintaining compliance',
      challenges: [
        'HIPAA and patient safety requirements',
        'Clinical system implementation complexity',
        'Multi-stakeholder coordination',
        'Resource constraints and burnout',
      ],
      benefits: [
        'HIPAA-compliant data handling and storage',
        'Clinical workflow templates',
        'Safety-first approval processes',
        'Resource capacity planning',
      ],
    },
    {
      icon: Code,
      industry: 'Technology',
      description: 'Scale product development with agile portfolio management',
      challenges: [
        'Managing hybrid Agile/Waterfall portfolios',
        'DevOps integration requirements',
        'Rapid innovation cycles',
        'Technical debt management',
      ],
      benefits: [
        'Flexible workflow configurations',
        'API integrations with dev tools',
        'Real-time portfolio prioritization',
        'Innovation pipeline tracking',
      ],
    },
    {
      icon: Factory,
      industry: 'Manufacturing',
      description: 'Optimize capital programs and operational excellence',
      challenges: [
        'Capital improvement project tracking',
        'Plant modernization initiatives',
        'Supply chain optimization',
        'Safety and environmental compliance',
      ],
      benefits: [
        'Capital project portfolio management',
        'Multi-site program coordination',
        'ROI and payback tracking',
        'Compliance monitoring tools',
      ],
    },
    {
      icon: Landmark,
      industry: 'Government',
      description: 'Execute public sector programs with transparency',
      challenges: [
        'Multi-stakeholder governance',
        'Public accountability requirements',
        'Budget constraints and oversight',
        'Long-term infrastructure programs',
      ],
      benefits: [
        'Transparent reporting frameworks',
        'Public sector workflow templates',
        'Budget tracking and forecasting',
        'Citizen engagement tools',
      ],
    },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2A3B4C] to-[#3E5365] text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="max-w-3xl">
              <h1 className="text-5xl mb-6">Industry-Specific Solutions</h1>
              <p className="text-xl text-[#D5D9DD] leading-relaxed">
                Tailored PPM solutions addressing unique challenges across diverse industries,
                with customizable workflows and compliance frameworks.
              </p>
            </div>
            <div className="flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-[#DAA520] opacity-20 blur-3xl rounded-full"></div>
                <img 
                  src="https://images.unsplash.com/photo-1573757056004-065ad36e2cf4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxlbnRlcnByaXNlJTIwdGVjaG5vbG9neXxlbnwxfHx8fDE3NjcwOTAxMTF8MA&ixlib=rb-4.1.0&q=80&w=1080"
                  alt="Enterprise Technology"
                  className="rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions by Industry */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-16">
            {solutions.map((solution, idx) => {
              const Icon = solution.icon;
              return (
                <div
                  key={idx}
                  className={`grid md:grid-cols-2 gap-12 items-start ${
                    idx % 2 === 1 ? 'md:flex-row-reverse' : ''
                  }`}
                >
                  {/* Industry Overview */}
                  <div className={idx % 2 === 1 ? 'md:order-2' : ''}>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 bg-[#DAA520] rounded-lg flex items-center justify-center">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h2 className="text-3xl text-[#2A3B4C]">{solution.industry}</h2>
                      </div>
                    </div>
                    <p className="text-xl text-[#556B7C] mb-8">{solution.description}</p>

                    {/* Challenges */}
                    <div className="bg-[#E8E9E9] rounded-lg p-6 mb-6">
                      <h3 className="text-lg text-[#2A3B4C] mb-4">Common Challenges</h3>
                      <ul className="space-y-2">
                        {solution.challenges.map((challenge, cIdx) => (
                          <li key={cIdx} className="flex items-start gap-2 text-sm text-[#556B7C]">
                            <span className="text-[#8B9BAB] mt-1">•</span>
                            <span>{challenge}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Karyva Benefits */}
                  <div className={idx % 2 === 1 ? 'md:order-1' : ''}>
                    <div className="bg-gradient-to-br from-[#2A3B4C] to-[#3E5365] rounded-lg p-8 text-white h-full">
                      <h3 className="text-xl mb-6">How Karyva Helps</h3>
                      <ul className="space-y-4">
                        {solution.benefits.map((benefit, bIdx) => (
                          <li key={bIdx} className="flex items-start gap-3">
                            <div className="w-6 h-6 bg-[#DAA520] rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-xs">✓</span>
                            </div>
                            <span className="text-[#D5D9DD]">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Common Features Across Industries */}
      <section className="py-20 bg-[#E8E9E9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl text-[#2A3B4C] mb-4">Universal Excellence</h2>
            <p className="text-xl text-[#556B7C] max-w-3xl mx-auto">
              Core capabilities that deliver value across all industries
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <UniversalFeature
              title="Customizable Workflows"
              description="Adapt approval processes, terminology, and workflows to match your organization's unique requirements"
            />
            <UniversalFeature
              title="Single-Tenant Security"
              description="Dedicated AWS environment ensuring complete data sovereignty and enterprise-grade security"
            />
            <UniversalFeature
              title="Unlimited Scalability"
              description="Support for unlimited users, projects, and portfolios as your organization grows"
            />
            <UniversalFeature
              title="24/7 Enterprise Support"
              description="Dedicated support team ensuring maximum uptime and rapid issue resolution"
            />
            <UniversalFeature
              title="Rapid Deployment"
              description="Go live in weeks with pre-configured templates and guided implementation"
            />
            <UniversalFeature
              title="Integration Ready"
              description="Connect with existing enterprise systems via APIs and standard protocols"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl text-[#2A3B4C] mb-6">
            Discover Your Industry Solution
          </h2>
          <p className="text-xl text-[#556B7C] mb-8">
            Talk to our team about customizing Karyva for your organization's specific needs
          </p>
          <Button
            onClick={() => onNavigate('contact')}
            className="bg-[#DAA520] hover:bg-[#B8860B] text-white px-8 py-6 text-lg"
          >
            Contact Karyva Team
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
}

function UniversalFeature({ title, description }: { title: string; description: string }) {
  return (
    <div className="bg-white rounded-lg p-6 border border-[#C4CDD5]">
      <h3 className="text-lg text-[#2A3B4C] mb-3">{title}</h3>
      <p className="text-sm text-[#556B7C] leading-relaxed">{description}</p>
    </div>
  );
}